import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { visitsAPI, activitiesAPI, equipmentAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Textarea } from "../../../shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../shared/components/ui/dialog";
import { toast } from "sonner";
import {
  Activity,
  LogOut,
  Plus,
  Clock,
  AlertTriangle,
  Wrench,
  Zap,
  Eye,
  ShieldAlert,
  ArrowLeft,
} from "lucide-react";
import {
  formatDateTime,
  formatTime,
  getTimeSince,
  PURPOSE_LABELS,
  ACTIVITY_LABELS,
} from "../../../shared/lib/utils";

const ACTIVITY_OPTIONS = [
  { value: "breaker_maintenance_started", label: "Breaker Maintenance Started", icon: Wrench },
  { value: "breaker_maintenance_completed", label: "Breaker Maintenance Completed", icon: Wrench },
  { value: "switching_performed", label: "Switching Performed", icon: Zap },
  { value: "equipment_isolated", label: "Equipment Isolated", icon: Zap },
  { value: "equipment_returned", label: "Equipment Returned to Service", icon: Zap },
  { value: "abnormal_condition", label: "Abnormal Condition Observed", icon: AlertTriangle },
  { value: "safety_issue", label: "Safety Issue", icon: ShieldAlert },
  { value: "near_miss", label: "Near Miss", icon: AlertTriangle },
  { value: "emergency_note", label: "Emergency Note", icon: AlertTriangle },
  { value: "other", label: "Other Activity", icon: Activity },
];

export default function ActiveVisitPage() {
  const navigate = useNavigate();
  const { visitId } = useParams();

  const [visit, setVisit] = useState(null);
  const [activities, setActivities] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Activity form state
  const [activityType, setActivityType] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState("");
  const [activityNotes, setActivityNotes] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);

  useEffect(() => {
    loadData();
  }, [visitId]);

  const loadData = async () => {
    try {
      const [visitRes, activitiesRes] = await Promise.all([
        visitsAPI.getById(visitId),
        activitiesAPI.getByVisit(visitId),
      ]);

      setVisit(visitRes.data);
      setActivities(activitiesRes.data);

      // Load equipment for the station
      if (visitRes.data?.station_id) {
        const equipRes = await equipmentAPI.getAll(visitRes.data.station_id);
        setEquipment(equipRes.data);
      }
    } catch (err) {
      toast.error("Failed to load visit data");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();

    if (!activityType) {
      toast.error("Please select an activity type");
      return;
    }

    setSubmitting(true);
    try {
      await activitiesAPI.create({
        visit_id: visitId,
        activity_type: activityType,
        equipment_id: selectedEquipment || null,
        notes: activityNotes || null,
        is_emergency: isEmergency,
      });

      toast.success("Activity logged");
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log activity");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setActivityType("");
    setSelectedEquipment("");
    setActivityNotes("");
    setIsEmergency(false);
  };

  const handleEmergencyNote = () => {
    setActivityType("emergency_note");
    setIsEmergency(true);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Visit not found</p>
        <Button onClick={() => navigate("/")} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const isActive = visit.status === "active";

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-32" data-testid="active-visit-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/")}
        className="mb-4 font-mono text-sm"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      {/* Visit Info Card */}
      <Card className="border-2 border-emerald-500 rounded-none mb-6">
        <CardHeader className="border-b border-border bg-emerald-500/5">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 font-heading">
              <div className="h-10 w-10 bg-emerald-500 flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" />
              </div>
              {visit.station_name}
            </CardTitle>
            <Badge
              className={`rounded-none font-mono text-xs ${
                isActive
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-500 text-white"
              }`}
            >
              {visit.status.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Entry Time
              </p>
              <p className="font-mono text-sm">{formatDateTime(visit.entry_time)}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Duration
              </p>
              <p className="font-mono text-sm">{getTimeSince(visit.entry_time)}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Purpose
              </p>
              <p className="font-mono text-sm">
                {PURPOSE_LABELS[visit.purpose] || visit.purpose}
              </p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Work Order
              </p>
              <p className="font-mono text-sm">
                {visit.work_order_number || "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activities Timeline */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#FF6B00]" />
            Activity Log
          </h2>
          {isActive && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
                  data-testid="add-activity-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Log Activity
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-2 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">Log Activity</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddActivity} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Activity Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={activityType} onValueChange={setActivityType}>
                      <SelectTrigger
                        className="h-12 rounded-none border-2 font-mono"
                        data-testid="activity-type-select"
                      >
                        <SelectValue placeholder="Select activity..." />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {ACTIVITY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="h-4 w-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Equipment
                    </Label>
                    <Select
                      value={selectedEquipment}
                      onValueChange={setSelectedEquipment}
                    >
                      <SelectTrigger
                        className="h-12 rounded-none border-2 font-mono"
                        data-testid="equipment-select"
                      >
                        <SelectValue placeholder="Select equipment (optional)..." />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {equipment.map((eq) => (
                          <SelectItem key={eq.id} value={eq.id}>
                            <span className="font-mono">{eq.equipment_id}</span>
                            <span className="text-muted-foreground ml-2">
                              - {eq.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Notes
                    </Label>
                    <Textarea
                      value={activityNotes}
                      onChange={(e) => setActivityNotes(e.target.value)}
                      placeholder="Additional details..."
                      className="rounded-none border-2 font-mono min-h-[80px]"
                      data-testid="activity-notes-textarea"
                    />
                  </div>

                  <div className="flex items-center space-x-3 p-3 bg-red-500/10 border-2 border-red-500">
                    <Checkbox
                      id="emergency"
                      checked={isEmergency}
                      onCheckedChange={setIsEmergency}
                      className="h-5 w-5 rounded-none"
                      data-testid="emergency-checkbox"
                    />
                    <Label
                      htmlFor="emergency"
                      className="font-body text-sm cursor-pointer text-red-600 dark:text-red-400"
                    >
                      Mark as Emergency (will alert supervisors)
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                    data-testid="submit-activity-btn"
                  >
                    {submitting ? <span className="spinner" /> : "Log Activity"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {activities.length === 0 ? (
          <Card className="border-2 border-dashed border-border rounded-none">
            <CardContent className="p-8 text-center">
              <Activity className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="font-body text-muted-foreground">
                No activities logged yet
              </p>
              {isActive && (
                <p className="font-mono text-xs text-muted-foreground mt-2">
                  Click "Log Activity" to record work performed
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <Card
                key={activity.id}
                className={`border-2 rounded-none ${
                  activity.is_emergency
                    ? "border-red-500 bg-red-500/5"
                    : "border-border"
                }`}
                data-testid={`activity-item-${activity.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="outline"
                          className={`rounded-none font-mono text-xs ${
                            activity.is_emergency
                              ? "border-red-500 text-red-500"
                              : ""
                          }`}
                        >
                          {ACTIVITY_LABELS[activity.activity_type] ||
                            activity.activity_type}
                        </Badge>
                        {activity.is_emergency && (
                          <Badge className="rounded-none bg-red-500 text-white font-mono text-xs">
                            EMERGENCY
                          </Badge>
                        )}
                      </div>
                      {activity.equipment_name && (
                        <p className="font-mono text-sm">
                          Equipment: {activity.equipment_name}
                        </p>
                      )}
                      {activity.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {activity.notes}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs text-muted-foreground">
                        {formatTime(activity.timestamp)}
                      </p>
                      <p className="font-mono text-xs text-muted-foreground">
                        {activity.user_name}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Exit Button */}
      {isActive && (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-auto flex gap-3">
          <Button
            onClick={() => navigate(`/exit/${visitId}`)}
            className="flex-1 md:flex-none h-14 rounded-sm bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-300 dark:text-slate-900 font-mono uppercase tracking-wider font-bold"
            data-testid="exit-station-btn"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Exit Station
          </Button>
        </div>
      )}

      {/* Emergency Button */}
      {isActive && (
        <button
          onClick={handleEmergencyNote}
          className="fixed bottom-36 md:bottom-24 right-4 h-14 w-14 rounded-full bg-red-500 text-white shadow-lg flex items-center justify-center z-50 emergency-btn"
          data-testid="emergency-btn"
        >
          <AlertTriangle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
